import type { FindingJumpTarget } from "../hooks/useReportQuery";
import { countWord, joinList, LOCATION_LABEL } from "./labels";
import type { StoneFinding, StoneLaterality } from "./types";

function itemText(s: StoneFinding): string {
  const mm = `${Math.round(s.measurements.longAxisMm)} mm`;
  return s.location ? `${mm} ${LOCATION_LABEL[s.location]}` : mm;
}

function sideClause(side: StoneLaterality | null, stones: StoneFinding[]): string {
  const sorted = [...stones].sort((a, b) => b.measurements.longAxisMm - a.measurements.longAxisMm);
  const noun = sorted.length === 1 ? "calculus" : "calculi";
  const sideWord = side ? `${side} ` : "";
  return `${countWord(sorted.length)} ${sideWord}renal ${noun} — ${joinList(sorted.map(itemText))}.`;
}

export function composeStoneFinding(approved: StoneFinding[]): {
  text: string;
  target: FindingJumpTarget | null;
} {
  if (approved.length === 0) return { text: "", target: null };
  const clauses: string[] = [];
  for (const side of ["left", "right"] as StoneLaterality[]) {
    const group = approved.filter((s) => s.laterality === side);
    if (group.length) clauses.push(sideClause(side, group));
  }
  const unsided = approved.filter((s) => s.laterality == null);
  if (unsided.length) clauses.push(sideClause(null, unsided));
  const largest = [...approved].sort(
    (a, b) => b.measurements.longAxisMm - a.measurements.longAxisMm,
  )[0];
  const target: FindingJumpTarget = {
    seriesId: largest.seriesInstanceUid,
    sliceIndex: largest.sliceIndex,
    frameOfReferenceUid: largest.frameOfReferenceUid,
  };
  // With multiple stones the per-item sizes are spread across clauses; call out
  // the largest explicitly. A single stone already states its size in-line.
  let text = clauses.join(" ");
  if (approved.length >= 2) {
    text += ` Largest calculus ${Math.round(largest.measurements.longAxisMm)} mm.`;
  }
  return { text, target };
}
